import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen pb-8" data-testid="privacy-policy-page">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/" className="text-primary hover:underline mb-4 inline-block" data-testid="back-to-home-link">
          ← Back to Home
        </Link>

        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Last Updated: May 20, 2026</p>

        <div className="space-y-6 text-left">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p className="mb-4">
              Welcome to GameFeed.io, operated by Schuler Consultants ("we," "us," or "our"). This Privacy Policy
              explains what information we collect about you, why we collect it, who we share it with, and the
              choices you have. It should be read alongside our{' '}
              <Link to="/cookie-policy" className="text-primary hover:underline" data-testid="cookie-policy-link">
                Cookie Policy
              </Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Our Commitment</h2>
            <p className="mb-4">
              We do not sell your personal information, and we do not use it for advertising or share it with
              advertisers. We use a small number of vendors (listed in section 6) to operate the site and understand
              how it's used. Outside of those vendors, your information is not shared with third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Information We Collect</h2>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.1 Account information</h3>
            <p className="mb-4">When you create an account, we collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Email address</li>
              <li>Name (or display name)</li>
              <li>Password — stored only as a salted hash; we never store, see, or transmit your raw password</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.2 Saved articles and preferences</h3>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Which articles you have saved, so we can show them back to you</li>
              <li>Your theme preference (light or dark mode)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.3 Analytics and usage information</h3>
            <p className="mb-4">
              We collect usage data through two analytics services. The full technical detail is in our Cookie Policy;
              in summary:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>
                <strong>Simple Analytics</strong> — cookieless, aggregate, anonymous traffic statistics. No personal
                information is collected.
              </li>
              <li>
                <strong>PostHog</strong> — product analytics. PostHog assigns an anonymous device identifier and records
                pages viewed, clicks and form interactions on our pages ("autocapture"), browser and device information,
                approximate location derived from IP address, referring URLs, and performance metrics. PostHog may also
                be used to capture anonymized session recordings, with form inputs and text content masked by default.
              </li>
            </ul>
            <p className="mb-4">
              PostHog data is tied to an anonymous device ID, not to your name or email. If you are signed in, we may
              associate your account with that device ID so we can analyze logged‑in behavior; you can opt out of this
              at any time (see section 8).
            </p>

            <h3 className="text-xl font-semibold mb-2 mt-4">3.4 Information we do not collect</h3>
            <p className="mb-4">
              We do not collect payment information, government IDs, precise GPS location, contacts, or social‑graph
              data. We do not buy data about you from data brokers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. How We Use Your Information</h2>
            <p className="mb-4">We use the information described above to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Create and maintain your account</li>
              <li>Let you save and access your favorite articles</li>
              <li>Operate, secure, and improve the site</li>
              <li>Diagnose bugs and performance problems</li>
              <li>Communicate with you about your account when necessary (for example, password resets or material changes to this policy)</li>
            </ul>
            <p className="mb-4">
              We do not use your information to build advertising profiles, sell to data brokers, or train external AI models.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Legal Basis (EU / UK Visitors)</h2>
            <p className="mb-4">
              If you are in the EU, UK, or EEA, we rely on the following legal bases under the GDPR:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Contract</strong> — to provide the account and saved‑articles features you have signed up for.</li>
              <li><strong>Consent</strong> — for non‑essential analytics (PostHog). You can withdraw consent at any time without affecting the lawfulness of processing before withdrawal.</li>
              <li><strong>Legitimate interests</strong> — for security logging, abuse prevention, and aggregate, cookieless analytics (Simple Analytics).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Third‑Party Services</h2>
            <p className="mb-4">
              We share information only with the following processors, and only for the purposes listed:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Simple Analytics</strong> (analytics) — anonymous traffic statistics.</li>
              <li><strong>PostHog, Inc.</strong> (product analytics, US) — pageviews, autocaptured events, device metadata, and optional session recordings. Acts as a data processor under a data‑processing agreement.</li>
              <li><strong>Our hosting and email providers</strong> — to serve the site and send account emails.</li>
            </ul>
            <p className="mb-4">
              When you click an external article link, you leave GameFeed.io and are taken to a third‑party site that
              has its own privacy policy. We are not responsible for those sites.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. International Transfers</h2>
            <p className="mb-4">
              Some of our processors (including PostHog) are located in the United States. Where data of EU/UK visitors
              is transferred to the US, we rely on the European Commission's Standard Contractual Clauses and equivalent
              UK transfer mechanisms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Your Choices and Rights</h2>
            <p className="mb-4">You can, at any time:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Access, correct, or delete your account</strong> from your account settings, or by emailing us.</li>
              <li>
                <strong>Opt out of PostHog analytics</strong> at{' '}
                <Link to="/cookie-preferences" className="text-primary hover:underline" data-testid="cookie-preferences-link">
                  /cookie-preferences
                </Link>{' '}
                or via the footer link. We honor the Global Privacy Control (GPC) signal — if your browser sends GPC, we treat that as an opt‑out.
              </li>
              <li><strong>Withdraw consent</strong> (EU/UK) at any time from the same controls.</li>
              <li><strong>Export your data</strong> by emailing us; we will send you a copy of your account information and saved articles.</li>
            </ul>
            <p className="mb-4">
              If you are in the EU, UK, or EEA, you also have the right to lodge a complaint with your local data
              protection authority. If you are in California, you have rights under the CCPA/CPRA including the right
              to know, the right to delete, and the right to opt out of "sale" or "sharing" — we do not sell or share
              personal information as those terms are defined under the CCPA/CPRA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Data Retention</h2>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Account data</strong> is kept for as long as your account exists. If you delete your account, we delete or anonymize it within 30 days, except where we are legally required to keep it.</li>
              <li><strong>Saved‑articles data</strong> is deleted with your account.</li>
              <li><strong>PostHog event data</strong> is retained for 12 months and then deleted or aggregated.</li>
              <li><strong>Simple Analytics data</strong> is aggregate and contains no personal information.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Data Security</h2>
            <p className="mb-4">
              Account data is stored using industry‑standard encryption in transit (TLS) and at rest. Passwords are
              salted and hashed. We apply technical and organizational measures appropriate to the size of our service
              to protect against unauthorized access, alteration, disclosure, or destruction. No system is perfectly
              secure; if we become aware of a breach affecting your data, we will notify you and the relevant authorities
              as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Children</h2>
            <p className="mb-4">
              GameFeed.io is not directed to children under 13 (or under 16 in the EU/UK), and we do not knowingly
              collect information from them. If you believe a child has provided us with personal information, contact
              us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Changes to This Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. When we do, we will update the "Last Updated" date
              at the top of this page. Material changes will be announced on the site, and where required by law we
              will obtain fresh consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">13. Contact</h2>
            <p className="mb-4">
              For privacy questions, requests, or complaints, contact us at:{' '}
              <a href="mailto:info@gamefeed.io" className="text-primary hover:underline" data-testid="contact-email-link">
                info@gamefeed.io
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
